import React from "react";
import { TextField, TextFieldProps, Typography } from "@mui/material";

interface CurrencyTextFieldProps extends Omit<TextFieldProps, 'value' | 'onChange' | 'onBlur' | 'type'> {
  value: number | null;
  onChange: (value: number | null) => void;
  onBlur?: (value: number | null) => void;
  locale?: string;
  currencySymbol?: string;
  maxWidth?: string | number;
}

// label שלא הוקטן (שדה ריק) לא עולה על הסימן בקצה השדה (endAdornment); MUI מקטין אוטומטית רק עם startAdornment.
// באפליקציה אין RTL plugin – ה-label ממוקם משמאל, בדיוק במקום הסימן, ולכן מזיזים אותו ימינה מעבר לסימן
// (רק כשאינו מוקטן, כדי שהחריץ במסגרת יישאר במקומו)
export const END_ADORNMENT_LABEL_SX = {
  "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": { left: 28, maxWidth: "calc(100% - 56px)" },
};

const CurrencyTextField: React.FC<CurrencyTextFieldProps> = ({
  value,
  onChange,
  onBlur,
  locale = 'he-IL',
  currencySymbol = '₪',
  inputProps,
  slotProps,
  sx,
  maxWidth = "200px",
  ...rest
}) => {
  const formatNumber = (num: number): string => {
    return num.toLocaleString(locale);
  };

  const parseFormattedNumber = (str: string): number | null => {
    const cleaned = str.replace(/,/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? null : parsed;
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsedValue = parseFormattedNumber(e.target.value);
    onChange(parsedValue);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const parsedValue = parseFormattedNumber(e.target.value);
    onBlur?.(parsedValue);
  };

  return (
    <TextField
      type="text"
      value={value === null ? '' : formatNumber(value)}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={onBlur ? handleBlur : undefined}
      inputProps={{
        inputMode: "numeric",
        dir: "ltr",
        style: { textAlign: "left" },
        ...inputProps,
      }}
      slotProps={{
        input: {
          endAdornment: <Typography sx={{ ml: 1 }}>{currencySymbol}</Typography>,
        },
        ...slotProps,
      }}
      sx={{ maxWidth, ...END_ADORNMENT_LABEL_SX, ...sx }}
      {...rest}
    />
  );
};

export default CurrencyTextField;
